// An invoice or a report as the server's PDF (#621): the `documentLink`
// callable, and what a failure of it says.
//
// The server renders the document only when what it shows has changed, keeps
// it, and answers with a thirty-day file link (functions/documents/link.js) —
// the same file the phones share. So there is nothing to cache here: every
// ask goes to the server, which is what notices a renamed room or a new
// accent colour.
//
// A PDF is made online. Offline, the call is not made at all, and the core's
// `offline` sentence says why, rather than the SDK's error some seconds later.

// Nothing here imports Firebase or React, so the UI suite loads it with the
// components that use it; the hook that binds it to the callable is
// hooks/useDocumentLink.js.

import { documentLinkErrorMessage, documentPdfWord } from '../domain/documentPdf';

export class DocumentLinkError extends Error {
  constructor(message) {
    super(message);
    this.name = 'DocumentLinkError';
  }
}

const browserOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);

/**
 * `(kind, id, { disposition }) => Promise<{ url, filename }>` over `call`,
 * the callable, or one that refuses when this build has no Functions. Every
 * refusal is a DocumentLinkError carrying the core's sentence.
 */
export function documentLinkFrom(call, { online = browserOnline } = {}) {
  return async (kind, id, { disposition = 'inline' } = {}) => {
    if (!online()) throw new DocumentLinkError(documentLinkErrorMessage('offline'));
    if (!call) throw new DocumentLinkError(documentPdfWord('not_set_up'));
    let res;
    try {
      res = await call({ kind, id, disposition });
    } catch (err) {
      // A refusal firebase-functions made itself (App Check, a token that
      // did not verify) already carries the core's sentence for it (#493).
      if (err?.unverified === true) throw new DocumentLinkError(err.message);
      throw new DocumentLinkError(documentLinkErrorMessage(err?.code || '', err?.message ?? null));
    }
    const url = res?.data?.url;
    if (typeof url !== 'string' || !url) throw new DocumentLinkError(documentLinkErrorMessage('failed'));
    return { url, filename: typeof res.data.filename === 'string' ? res.data.filename : null };
  };
}

/**
 * Opens a document's PDF in a new tab. The tab is opened **before** the
 * call, inside the click — a tab opened after a round trip that may include
 * a render is one a browser's pop-up blocker refuses — and pointed at the
 * link once it comes. A failure closes it again. Resolves to `{ url, opened }`:
 * `opened` false when the browser gave no tab, and the caller then offers the
 * link itself.
 */
export async function openDocumentPdf(documentLink, kind, id, { open = (...a) => window.open(...a) } = {}) {
  const tab = open('', '_blank');
  try {
    const { url } = await documentLink(kind, id);
    if (tab && !tab.closed) {
      tab.location.href = url;
      return { url, opened: true };
    }
    return { url, opened: false };
  } catch (err) {
    if (tab && !tab.closed) tab.close();
    throw err;
  }
}

/**
 * Saves a document's PDF: the link asks for the bytes as an attachment, so
 * following it downloads the file under its own name and leaves the page
 * where it is.
 */
export async function downloadDocumentPdf(documentLink, kind, id, { follow = followLink } = {}) {
  const { url, filename } = await documentLink(kind, id, { disposition: 'attachment' });
  follow(url);
  return { url, filename };
}

function followLink(url) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
